import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnInit, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { ScreeningHelpComponent } from './molecules/help/help.component';
import { Router } from '@angular/router';
import { SocialScreeningService } from '../../services/social-screening/social-screening.service';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { GetScreeningBriefsDto, ScreeningBriefResponseDto, ScreeningKanBanStatusEnum, ScreeningListResponseDto } from '../../common/general-types/social-screening.dto';
import { KanbanCardComponent } from './molecules/kanban-card/kanban-card.component';
import { RecruitGroupPicker } from '@/app/molecules/recruit-group-picker/recruit-group-picker';

@Component({
  selector: 'app-screening-briefs',
  standalone: true,
  imports: [FormField, ScreeningHelpComponent, KanbanCardComponent, RecruitGroupPicker],
  templateUrl: './screening-briefs.component.html',
  styleUrl: './screening-briefs.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ScreeningBriefsComponent implements OnInit {

  /** Layout */
  selectedTab = signal<'creator-lists' | 'screening-briefs'>('screening-briefs');
  showHelpModal = signal<boolean>(false);
  showAddNewDropdown = signal<boolean>(false);
  /** Layout - END */

  private router = inject(Router);
  public socialScreeningService = inject(SocialScreeningService);
  private currentUserService = inject(CurrentUserService);

  private isInitialized = false;

  private subscriptions = new Subscription();
  private searchQuerySubject = new Subject<string>();
  searchQueryModel = signal({
    query: '',
  })
  searchQueryForm = form(this.searchQueryModel)

  /** Screening Lists */
  allLists = signal<ScreeningListResponseDto[]>([]);
  /** Screening Lists - END */

  /** Screening Briefs */
  notReadyFilter = signal<GetScreeningBriefsDto>({
    businessAccountId: this.currentUserService.focussedBrand.id,
    take: 10,
    skip: 0,
    status: ScreeningKanBanStatusEnum.Not_Ready,
  });
  notReadyBriefs = signal<ScreeningBriefResponseDto[]>([]);

  processingFilter = signal<GetScreeningBriefsDto>({
    businessAccountId: this.currentUserService.focussedBrand.id,
    take: 10,
    skip: 0,
    status: ScreeningKanBanStatusEnum.Processing,
  });
  processingBriefs = signal<ScreeningBriefResponseDto[]>([]);

  refinementFilter = signal<GetScreeningBriefsDto>({
    businessAccountId: this.currentUserService.focussedBrand.id,
    take: 10,
    skip: 0,
    status: ScreeningKanBanStatusEnum.Refinement,
  });
  refinementBriefs = signal<ScreeningBriefResponseDto[]>([]);

  screeningFilter = signal<GetScreeningBriefsDto>({
    businessAccountId: this.currentUserService.focussedBrand.id,
    take: 10,
    skip: 0,
    status: ScreeningKanBanStatusEnum.Screening,
  });
  screeningBriefs = signal<ScreeningBriefResponseDto[]>([]);

  completeFilter = signal<GetScreeningBriefsDto>({
    businessAccountId: this.currentUserService.focussedBrand.id,
    take: 10,
    skip: 0,
    status: ScreeningKanBanStatusEnum.Complete,
  });
  completeBriefs = signal<ScreeningBriefResponseDto[]>([]);
  /** Screening Briefs - END */

  constructor() {
    this.subscriptions.add(
      this.searchQuerySubject.pipe(debounceTime(350)).subscribe(query => {
        console.log('query changed:', query);
        if (this.isInitialized) {

        }
      })
    );

    effect(() => {
      const query = this.searchQueryModel().query;
      this.searchQuerySubject.next(query);
    });
  }

  ngOnInit(): void {
    this.getSocialScreeningBriefs();
    this.getSocialScreeningLists();
  }

  getSocialScreeningBriefs() {
    this.getNotReadyBriefs();
    this.getProcessingBriefs();
    this.getRefinementBriefs();
    this.getScreeningBriefs();
    this.getCompleteBriefs();
  }

  getNotReadyBriefs() {
    this.socialScreeningService.getSocialScreeningBriefs(this.notReadyFilter()).subscribe((notReadyRes: ScreeningBriefResponseDto[]) => {
      this.notReadyBriefs.set(notReadyRes);
      console.log('Not Ready Briefs:', notReadyRes);
      this.notReadyFilter.update(filter => ({ ...filter, skip: filter.skip + filter.take }));
    });
  }

  getProcessingBriefs() {
    this.socialScreeningService.getSocialScreeningBriefs(this.processingFilter()).subscribe((processingRes: ScreeningBriefResponseDto[]) => {
      this.processingBriefs.set(processingRes);
      console.log('Processing Briefs:', processingRes);
      this.processingFilter.update(filter => ({ ...filter, skip: filter.skip + filter.take }));
    });
  }

  getRefinementBriefs() {
    this.socialScreeningService.getSocialScreeningBriefs(this.refinementFilter()).subscribe((refinementRes: ScreeningBriefResponseDto[]) => {
      this.refinementBriefs.set(refinementRes);
      console.log('Refinement Briefs:', refinementRes);
      this.refinementFilter.update(filter => ({ ...filter, skip: filter.skip + filter.take }));
    });
  }

  getScreeningBriefs() {
    this.socialScreeningService.getSocialScreeningBriefs(this.screeningFilter()).subscribe((screeningRes: ScreeningBriefResponseDto[]) => {
      this.screeningBriefs.set(screeningRes);
      console.log('Screening Briefs:', screeningRes);
      this.screeningFilter.update(filter => ({ ...filter, skip: filter.skip + filter.take }));
    });
  }

  getCompleteBriefs() {
    this.socialScreeningService.getSocialScreeningBriefs(this.completeFilter()).subscribe((completeRes: ScreeningBriefResponseDto[]) => {
      this.completeBriefs.set(completeRes);
      console.log('Complete Briefs:', completeRes);
      this.completeFilter.update(filter => ({ ...filter, skip: filter.skip + filter.take }));
    });
  }

  getSocialScreeningLists() {
    this.socialScreeningService.getBusinessAccountSocialScreeningLists(this.currentUserService.focussedBrand.id).subscribe((response) => {
      console.log('Social Screening Lists:', response);
      this.allLists.set(response as ScreeningListResponseDto[]);
    });
  }

  newCreatorList() {
    this.router.navigate(['/creators-my-groups']);
  }

  newScreeningBrief() {
    this.router.navigate(['/screening-brief-create']);
  }

  selectTabMobile(event: any) {
    console.log('selectTabMobile()', event);
    this.selectTab(event.target.value as 'creator-lists' | 'screening-briefs');
  }

  selectTab(tab: 'creator-lists' | 'screening-briefs') {
    this.selectedTab.set(tab);
  }

  getTabClass(tab: 'creator-lists' | 'screening-briefs') {
    return this.selectedTab() === tab 
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200' 
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  viewList(list: ScreeningListResponseDto) {
    console.log('View list:', list);
    // TODO: Navigate to list detail page or edit page
    // this.router.navigate(['/screening-creator-list-edit', list.id]);
  }

  formatListDate(list: ScreeningListResponseDto): string {
    // Check if list has a date field (createdDate, createdAt, etc.)
    const date = (list as any).createdDate || (list as any).createdAt || (list as any).dateCreated;
    if (!date) {
      return 'No date';
    }

    const dateObj = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(dateObj);
  }
}
